import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { currency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Package, Heart, Home as HomeIcon, User as UserIcon, Sparkles } from "lucide-react";
import CustomOrderDetail, { StatusStrip } from "@/components/CustomOrderDetail";

const emptyAddress = { label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India", is_default: false };

export default function CustomerDashboard() {
  const { user, loading, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [tab, setTab] = useState(params.get("tab") || "orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [openCustom, setOpenCustom] = useState(null);
  const [newAddr, setNewAddr] = useState(emptyAddress);
  const [profileForm, setProfileForm] = useState({ name: "" });

  useEffect(() => { if (!loading && !user) nav("/auth"); }, [user, loading, nav]);
  useEffect(() => { if (user) setProfileForm({ name: user.name || "" }); }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    api.get("/custom-orders").then((r) => setCustomOrders(r.data)).catch(() => {});
  }, [user, tab]);

  useEffect(() => { setParams({ tab }); }, [tab]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try { await api.put("/profile", profileForm); await refresh(); toast.success("Profile updated"); } catch { toast.error("Please try again"); }
  };
  const addAddress = async (e) => {
    e.preventDefault();
    try { await api.post("/profile/addresses", newAddr); await refresh(); setNewAddr(emptyAddress); toast.success("Address saved"); } catch { toast.error("Please try again"); }
  };
  const deleteAddress = async (id) => { await api.delete(`/profile/addresses/${id}`); await refresh(); };
  const removeWishlist = async (id) => { await api.delete(`/wishlist/${id}`); await refresh(); setWishlist((w) => w.filter((p) => p.id !== id)); };

  if (!user) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="account-page">
      <div className="border-b divider-gold pb-8 mb-12">
        <div className="overline text-emerald mb-3">Your studio</div>
        <h1 className="font-serif text-5xl tracking-tight">Hello, {user.name.split(" ")[0]}.</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
        <TabsList className="flex lg:flex-col h-auto items-stretch justify-start gap-1 bg-transparent p-0 overflow-x-auto lg:overflow-visible">
          <TabsTrigger data-testid="tab-orders" value="orders" className="justify-start data-[state=active]:bg-ivory data-[state=active]:text-emerald rounded-none px-4 py-3 whitespace-nowrap"><Package className="w-4 h-4 mr-2" /> Orders</TabsTrigger>
          <TabsTrigger data-testid="tab-custom" value="custom" className="justify-start data-[state=active]:bg-ivory data-[state=active]:text-emerald rounded-none px-4 py-3 whitespace-nowrap"><Sparkles className="w-4 h-4 mr-2" /> Custom orders</TabsTrigger>
          <TabsTrigger data-testid="tab-wishlist" value="wishlist" className="justify-start data-[state=active]:bg-ivory data-[state=active]:text-emerald rounded-none px-4 py-3 whitespace-nowrap"><Heart className="w-4 h-4 mr-2" /> Wishlist</TabsTrigger>
          <TabsTrigger data-testid="tab-addresses" value="addresses" className="justify-start data-[state=active]:bg-ivory data-[state=active]:text-emerald rounded-none px-4 py-3 whitespace-nowrap"><HomeIcon className="w-4 h-4 mr-2" /> Addresses</TabsTrigger>
          <TabsTrigger data-testid="tab-profile" value="profile" className="justify-start data-[state=active]:bg-ivory data-[state=active]:text-emerald rounded-none px-4 py-3 whitespace-nowrap"><UserIcon className="w-4 h-4 mr-2" /> Profile</TabsTrigger>
        </TabsList>

        <div>
          <TabsContent value="orders">
            <h2 className="font-serif text-2xl mb-6">Your orders</h2>
            {orders.length === 0 ? (
              <div className="py-16 text-charcoal/50 italic">No orders yet — <Link to="/shop" className="text-emerald not-italic">browse the shop</Link>.</div>
            ) : (
              <div className="space-y-6">
                {orders.map((o) => (
                  <div key={o.id} className="p-6 bg-ivory/40 border divider-gold" data-testid={`order-${o.order_number}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div>
                        <div className="font-serif text-xl">Order #{o.order_number}</div>
                        <div className="text-xs text-charcoal/60 mt-1">Placed {new Date(o.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="overline text-emerald">{o.status}</div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                      <div className="flex gap-3 flex-wrap">
                        {o.items.slice(0, 4).map((i) => (
                          <img key={i.product_id} src={i.image} alt="" className="w-12 h-14 object-cover bg-ivory" />
                        ))}
                        {o.items.length > 4 && <div className="text-xs text-charcoal/60">+{o.items.length - 4} more</div>}
                      </div>
                      <div className="text-right">
                        <div className="font-serif text-xl">{currency(o.total)}</div>
                        <div className="text-xs text-charcoal/60">{o.payment_method === "cod" ? "Cash on delivery" : "Card"}</div>
                      </div>
                    </div>
                    <div className="mt-5 pt-4 border-t divider-gold">
                      <div className="overline text-charcoal/60 mb-2">Timeline</div>
                      <div className="flex flex-wrap gap-3 text-xs">
                        {(o.timeline || []).map((t, i) => (
                          <div key={i} className="px-3 py-1 bg-ivory">{t.status} · {new Date(t.at).toLocaleDateString()}</div>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const html = `<!doctype html><html><head><title>Invoice ${o.order_number}</title><style>body{font-family:Georgia,serif;padding:40px;color:#222}h1{font-weight:400;font-size:32px}table{width:100%;border-collapse:collapse;margin-top:24px}td,th{padding:8px 4px;border-bottom:1px solid #ddd;text-align:left}</style></head><body><h1>Crystal Clear Craft</h1><p>Invoice · #${o.order_number}</p><p>${new Date(o.created_at).toLocaleString()}</p><table><thead><tr><th>Item</th><th>Qty</th><th>Total</th></tr></thead><tbody>${o.items.map(i => `<tr><td>${i.name}</td><td>${i.quantity}</td><td>₹${i.line_total}</td></tr>`).join("")}</tbody></table><p style="text-align:right;margin-top:24px">Subtotal: ₹${o.subtotal}<br/>Shipping: ₹${o.shipping}<br/>Tax: ₹${o.tax}<br/><b>Total: ₹${o.total}</b></p></body></html>`;
                        const w = window.open("", "_blank");
                        if (w) { w.document.write(html); w.document.close(); }
                      }}
                      data-testid={`invoice-${o.order_number}`}
                      className="mt-4 overline text-emerald"
                    >Download invoice →</button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="custom">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Your custom requests</h2>
              <Link to="/custom-order" data-testid="new-custom" className="overline text-emerald">+ New commission</Link>
            </div>
            {customOrders.length === 0 ? (
              <div className="py-16 text-charcoal/50 italic">No custom requests yet — <Link to="/custom-order" className="text-emerald not-italic">commission a piece</Link>.</div>
            ) : (
              <div className="space-y-4">
                {customOrders.map((co) => (
                  <button key={co.id} onClick={() => setOpenCustom(co.id)} className="w-full text-left p-5 bg-ivory/40 border divider-gold hover:border-emerald" data-testid={`co-row-${co.request_number}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div>
                        <div className="font-serif text-lg">#{co.request_number}</div>
                        <div className="text-xs text-charcoal/60 mt-1">{co.product_type} · {new Date(co.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="overline text-emerald text-[10px]">{co.status.replace(/_/g, " ")}</div>
                    </div>
                    <p className="text-sm text-charcoal/70 mt-2 line-clamp-2">{co.description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <StatusStrip status={co.status} />
                      {co.quote_price && <div className="font-serif">{currency(co.quote_price)}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {openCustom && <CustomOrderDetail orderId={openCustom} onClose={() => setOpenCustom(null)} onChange={() => api.get("/custom-orders").then((r) => setCustomOrders(r.data))} />}
          </TabsContent>


          <TabsContent value="wishlist">
            <h2 className="font-serif text-2xl mb-6">Saved for later</h2>
            {wishlist.length === 0 ? (
              <div className="py-16 text-charcoal/50 italic">Nothing saved yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {wishlist.map((p) => (
                  <div key={p.id} className="group" data-testid={`wl-${p.slug}`}>
                    <Link to={`/product/${p.slug}`} className="block img-hover aspect-[3/4] bg-ivory">
                      <img src={p.images?.[0]} alt="" className="w-full h-full object-cover" />
                    </Link>
                    <div className="mt-3 flex items-start justify-between">
                      <div>
                        <div className="font-serif">{p.name}</div>
                        <div className="text-sm text-charcoal/60">{currency(p.price)}</div>
                      </div>
                      <button onClick={() => removeWishlist(p.id)} className="text-charcoal/50 hover:text-emerald"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="addresses">
            <h2 className="font-serif text-2xl mb-6">Delivery addresses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {(user.addresses || []).map((a) => (
                <div key={a.id} className="p-5 bg-ivory/40 border divider-gold" data-testid={`addr-${a.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="font-serif text-lg">{a.label}{a.is_default && <span className="ml-2 overline text-emerald text-[10px]">Default</span>}</div>
                    <button onClick={() => deleteAddress(a.id)} className="text-charcoal/50 hover:text-emerald"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="text-sm text-charcoal/70 mt-2">
                    {a.full_name}<br />{a.phone}<br />
                    {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                    {a.city}, {a.state} {a.postal_code}<br />{a.country}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={addAddress} className="space-y-3 max-w-lg">
              <div className="overline text-emerald">Add new address</div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Label (Home/Work)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} data-testid="na-label" />
                <Input placeholder="Full name" required value={newAddr.full_name} onChange={(e) => setNewAddr({ ...newAddr, full_name: e.target.value })} data-testid="na-name" />
                <Input placeholder="Phone" required value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} data-testid="na-phone" />
                <Input placeholder="Postal code" required value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} data-testid="na-zip" />
                <Input placeholder="Line 1" required value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} className="col-span-2" data-testid="na-line1" />
                <Input placeholder="City" required value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} data-testid="na-city" />
                <Input placeholder="State" required value={newAddr.state} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} data-testid="na-state" />
              </div>
              <Button data-testid="na-submit" type="submit" className="rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Save address</Button>
            </form>
          </TabsContent>

          <TabsContent value="profile">
            <h2 className="font-serif text-2xl mb-6">Profile</h2>
            <form onSubmit={saveProfile} className="max-w-md space-y-4">
              <div><label className="overline text-charcoal/60 mb-2 block">Name</label><Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="prof-name" /></div>
              <div><label className="overline text-charcoal/60 mb-2 block">Email</label><Input value={user.email} disabled /></div>
              <Button data-testid="prof-save" type="submit" className="rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Save</Button>
            </form>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
